import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi, buildApiUrl, getApiHeaders, getCSRFHeaders } from '../../lib/api';
import { Button, Card, Input } from '../../components/ui';

// Custom debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

interface Attendee {
  id: string;
  name: string;
  email: string;
  affiliation?: string | null;
  qrCode: string;
  registeredAt: string;
  checkInCount: number;
}

interface CheckIn {
  id: string;
  sessionId: string;
  sessionTitle: string;
  sessionStartTime: string;
  checkedInAt: string;
}

interface AttendeeSubmitData {
  name: string;
  email: string;
  affiliation?: string;
}

interface AttendeeFormData {
  name: string;
  email: string;
  affiliation: string;
}

interface ImportResult {
  successCount: number;
  duplicateCount: number;
  errorCount: number;
  errors: Array<{ row: number; message: string }>;
  duplicates: Array<{ row: number; email: string }>;
}

export default function AttendeeManager() {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAttendee, setEditingAttendee] = useState<Attendee | null>(null);
  const [viewingCheckIns, setViewingCheckIns] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;
  const [qrCodeView, setQrCodeView] = useState<{ 
    attendeeId: string; 
    attendeeName: string;
    qrCode: string;
    qrCodeImage?: string;
    qrData?: string;
  } | null>(null);
  const [formData, setFormData] = useState<AttendeeFormData>({
    name: '',
    email: '',
    affiliation: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof AttendeeFormData, string>>>({});
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{
    isUploading: boolean;
    result: ImportResult | null;
  } | null>(null);

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Fetch attendees with search and pagination
  // keepPreviousData prevents the full-page spinner when search/page changes,
  // so the input stays mounted and keeps focus.
  const { data: response, isLoading: attendeesLoading, isFetching } = useQuery<{ data: Attendee[]; pagination: any }>({
    queryKey: ['admin', 'attendees', debouncedSearchTerm, currentPage],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('page', String(currentPage));
      params.set('limit', String(PAGE_SIZE));
      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      return fetchApi<{ data: Attendee[]; pagination: any }>(`/admin/attendees?${params.toString()}`);
    },
    placeholderData: (previousData) => previousData,
  });

  const attendees = response?.data;
  const pagination = response?.pagination;

  // Fetch check-ins for a specific attendee
  const { data: checkIns, isLoading: checkInsLoading } = useQuery<CheckIn[]>({
    queryKey: ['admin', 'checkins', 'attendee', viewingCheckIns],
    queryFn: () => fetchApi<CheckIn[]>(`/admin/checkins/attendee/${viewingCheckIns}`),
    enabled: !!viewingCheckIns,
  });

  // Delete check-in mutation
  const deleteCheckInMutation = useMutation({
    mutationFn: (checkInId: string) =>
      fetchApi(`/admin/checkins/${checkInId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'checkins'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'attendees'] });
      showNotification('success', 'Check-in deleted successfully');
    },
    onError: (error: Error) => {
      showNotification('error', `Failed to delete check-in: ${error.message}`);
    },
  });

  // Delete attendee mutation
  const deleteAttendeeMutation = useMutation({
    mutationFn: (id: string) =>
      fetchApi(`/admin/attendees/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'attendees'] });
      showNotification('success', 'Attendee deleted successfully');
    },
    onError: (error: Error) => {
      showNotification('error', `Failed to delete attendee: ${error.message}`);
    },
  });

  // Create attendee mutation
  const createMutation = useMutation({
    mutationFn: (data: AttendeeSubmitData) =>
      fetchApi('/admin/attendees', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'attendees'] });
      showNotification('success', 'Attendee created successfully');
      resetForm();
    },
    onError: (error: Error) => {
      showNotification('error', `Failed to create attendee: ${error.message}`);
    },
  });

  // Update attendee mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: AttendeeSubmitData }) =>
      fetchApi(`/admin/attendees/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'attendees'] });
      showNotification('success', 'Attendee updated successfully');
      resetForm();
    },
    onError: (error: Error) => {
      showNotification('error', `Failed to update attendee: ${error.message}`);
    },
  });

  // Export attendees mutation
  const exportMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(buildApiUrl('/admin/attendees/export'), {
        headers: getApiHeaders(false), // No Content-Type for file download
      });
      
      if (!response.ok) {
        throw new Error('Failed to export attendees');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'attendees-export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
    onSuccess: () => {
      showNotification('success', 'Attendee data exported successfully');
    },
    onError: (error: Error) => {
      showNotification('error', `Failed to export attendees: ${error.message}`);
    },
  });

  // CSV import mutation
  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(buildApiUrl('/admin/attendees/import'), {
        method: 'POST',
        headers: {
          ...getApiHeaders(false), // No Content-Type for FormData (browser sets it with boundary)
          ...getCSRFHeaders(),
        },
        body: formData,
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Upload failed');
      }
      
      return response.json();
    },
    onSuccess: (result: ImportResult) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'attendees'] });
      setUploadProgress({ isUploading: false, result });
      setUploadFile(null);
      showNotification('success', `Successfully imported ${result.successCount} attendee(s)`);
    },
    onError: (error: Error) => {
      showNotification('error', `Upload failed: ${error.message}`);
      setUploadProgress(null);
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      setUploadProgress(null);
    }
  };

  const handleUpload = () => {
    if (uploadFile) {
      // Clear previous results when new upload begins (Requirement 9.6)
      setUploadProgress({ isUploading: true, result: null });
      importMutation.mutate(uploadFile);
    }
  };

  const handleTemplateDownload = async () => {
    try {
      const response = await fetch(buildApiUrl('/admin/attendees/template'), {
        headers: getApiHeaders(false),
      });
      
      if (!response.ok) {
        throw new Error('Failed to download template');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'attendee-upload-template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      showNotification('error', 'Failed to download template');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof AttendeeFormData, string>> = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = 'Invalid email format';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData: AttendeeSubmitData = {
      name: formData.name,
      email: formData.email,
      affiliation: formData.affiliation.trim() || undefined,
    };

    if (editingAttendee) {
      updateMutation.mutate({ id: editingAttendee.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleEdit = (attendee: Attendee) => {
    setEditingAttendee(attendee);
    setFormData({
      name: attendee.name,
      email: attendee.email,
      affiliation: attendee.affiliation || '',
    });
    setFormErrors({});
    setIsFormOpen(true);
  };

  const handleViewQRCode = async (attendeeId: string) => {
    try {
      const response = await fetchApi<{ 
        qrCode: string;
        qrCodeImage: string;
        attendeeId: string;
        attendeeName: string;
        qrData: string;
      }>(`/admin/attendees/${attendeeId}/qr`);
      setQrCodeView({ 
        attendeeId: response.attendeeId,
        attendeeName: response.attendeeName,
        qrCode: response.qrCode,
        qrCodeImage: response.qrCodeImage,
        qrData: response.qrData
      });
    } catch (error) {
      showNotification('error', 'Failed to load QR code');
    }
  };

  const handleViewCheckIns = (attendeeId: string) => {
    setViewingCheckIns(attendeeId);
  };

  const handleDeleteCheckIn = (checkInId: string) => {
    if (window.confirm('Are you sure you want to delete this check-in record? This action cannot be undone.')) {
      deleteCheckInMutation.mutate(checkInId);
    }
  };

  const handleDeleteAttendee = (attendee: Attendee) => {
    if (window.confirm(`Are you sure you want to delete "${attendee.name}" and all their check-in records? This action cannot be undone.`)) {
      deleteAttendeeMutation.mutate(attendee.id);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      affiliation: '',
    });
    setFormErrors({});
    setEditingAttendee(null);
    setIsFormOpen(false);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (attendeesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-usc-garnet mx-auto"></div>
          <p className="mt-4 text-usc-gray-600">Loading attendees...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-3xl font-bold text-usc-garnet">Attendee Management</h2>
            <p className="text-usc-gray-600 mt-2">Manage conference attendees and registrations</p>
          </div>
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={() => exportMutation.mutate()}
              disabled={exportMutation.isPending}
            >
              {exportMutation.isPending ? 'Exporting...' : 'Export Data'}
            </Button>
            <Button onClick={() => setIsFormOpen(true)}>
              + Create Attendee
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative max-w-md">
          <Input
            placeholder="Search by name, email, or affiliation..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {isFetching && !attendeesLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-usc-garnet"></div>
            </div>
          )}
        </div>
      </div>

      {/* CSV Upload Section */}
      <Card className="mb-6 p-6">
        <h3 className="text-xl font-bold text-usc-garnet mb-4">Bulk Upload from CSV</h3>
        
        <div className="space-y-4">
          {/* File Input */}
          <div>
            <label className="block text-sm font-medium text-usc-gray-700 mb-2">
              Select CSV File
            </label>
            <input
              type="file"
              accept=".csv,text/csv,application/csv"
              onChange={handleFileSelect}
              disabled={uploadProgress?.isUploading}
              className="block w-full text-sm text-usc-gray-900 border border-usc-gray-300 rounded-lg cursor-pointer bg-usc-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="csv-file-input"
            />
            {uploadFile && (
              <p className="mt-2 text-sm text-usc-gray-600" data-testid="file-info">
                Selected: {uploadFile.name} ({formatFileSize(uploadFile.size)})
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <Button
              onClick={handleUpload}
              disabled={!uploadFile || uploadProgress?.isUploading}
              data-testid="upload-button"
            >
              {uploadProgress?.isUploading ? 'Uploading...' : 'Upload CSV'}
            </Button>
            <button
              type="button"
              onClick={handleTemplateDownload}
              className="text-sm text-usc-cardinal hover:text-usc-garnet underline"
              data-testid="template-download-link"
            >
              Download CSV Template
            </button>
          </div>

          {/* Upload Results */}
          {uploadProgress?.result && (
            <div className="mt-4 space-y-3">
              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">Upload Summary</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700 font-medium">Success:</span>{' '}
                    <span className="text-blue-900">{uploadProgress.result.successCount}</span>
                  </div>
                  <div>
                    <span className="text-blue-700 font-medium">Duplicates:</span>{' '}
                    <span className="text-blue-900">{uploadProgress.result.duplicateCount}</span>
                  </div>
                  <div>
                    <span className="text-blue-700 font-medium">Errors:</span>{' '}
                    <span className="text-blue-900">{uploadProgress.result.errorCount}</span>
                  </div>
                </div>
              </div>

              {/* Validation Errors */}
              {uploadProgress.result.errors.length > 0 && (
                <details className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <summary className="font-semibold text-red-900 cursor-pointer">
                    Validation Errors ({uploadProgress.result.errors.length})
                  </summary>
                  <ul className="mt-3 space-y-1 text-sm text-red-800">
                    {uploadProgress.result.errors.map((error, index) => (
                      <li key={index}>
                        Row {error.row}: {error.message}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {/* Duplicates */}
              {uploadProgress.result.duplicates.length > 0 && (
                <details className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <summary className="font-semibold text-yellow-900 cursor-pointer">
                    Duplicate Emails ({uploadProgress.result.duplicates.length})
                  </summary>
                  <ul className="mt-3 space-y-1 text-sm text-yellow-800">
                    {uploadProgress.result.duplicates.map((duplicate, index) => (
                      <li key={index}>
                        Row {duplicate.row}: {duplicate.email}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Notification Toast */}
      {notification && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            notification.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {notification.message}
        </div>
      )}

      {/* Attendee Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-2xl font-bold text-usc-garnet mb-6">
                {editingAttendee ? 'Edit Attendee' : 'Create New Attendee'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  error={formErrors.name}
                  placeholder="Full name"
                />

                <Input
                  label="Email *"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  error={formErrors.email}
                  placeholder="email@example.com"
                />

                <Input
                  label="Affiliation"
                  value={formData.affiliation}
                  onChange={(e) => setFormData({ ...formData, affiliation: e.target.value })}
                  placeholder="Organization or institution"
                />

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                  >
                    {createMutation.isPending || updateMutation.isPending
                      ? 'Saving...'
                      : editingAttendee
                      ? 'Update Attendee'
                      : 'Create Attendee'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {/* QR Code Modal */}
      {qrCodeView && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <div className="p-6 text-center">
              <h3 className="text-2xl font-bold text-usc-garnet mb-2">{qrCodeView.attendeeName}</h3>
              <p className="text-sm text-usc-gray-600 mb-6">Check-in QR Code</p>
              
              {/* QR Code Image */}
              {qrCodeView.qrCodeImage && (
                <div className="bg-white p-8 rounded-lg border-2 border-usc-gray-200 mb-4 flex justify-center">
                  <img 
                    src={qrCodeView.qrCodeImage} 
                    alt={`QR Code for ${qrCodeView.attendeeName}`}
                    className="w-80 h-80"
                    style={{ imageRendering: 'pixelated' }}
                  />
                </div>
              )}

              {/* Instructions */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4 text-left">
                <p className="text-sm text-blue-900 font-semibold mb-2">How to use:</p>
                <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Go to the Check-ins page</li>
                  <li>Select a session</li>
                  <li>Click "Start Scanning"</li>
                  <li>Point your camera at this QR code</li>
                  <li>Check-in will be recorded automatically</li>
                </ol>
              </div>

              {/* Manual Testing Option */}
              <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 mb-4 text-left">
                <p className="text-xs text-yellow-900 font-semibold mb-1">For Testing (Manual Check-in):</p>
                <p className="text-xs text-yellow-800 mb-2">Copy this Attendee ID:</p>
                <div className="bg-white p-2 rounded border border-yellow-300 font-mono text-xs break-all">
                  {qrCodeView.attendeeId}
                </div>
                <p className="text-xs text-yellow-700 mt-2">
                  Paste it in the "Manual Check-in" field on the Check-ins page
                </p>
              </div>

              <Button onClick={() => setQrCodeView(null)}>
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Check-ins History Modal */}
      {viewingCheckIns && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-usc-garnet">Check-in History</h3>
                <Button variant="outline" onClick={() => setViewingCheckIns(null)}>
                  Close
                </Button>
              </div>

              {checkInsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-usc-garnet"></div>
                </div>
              ) : checkIns && checkIns.length > 0 ? (
                <div className="space-y-3">
                  {checkIns.map((checkIn) => (
                    <div
                      key={checkIn.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex-1">
                        <h4 className="font-semibold text-usc-garnet">{checkIn.sessionTitle}</h4>
                        <div className="text-sm text-gray-600 mt-1">
                          <div>
                            Session: {formatDateTime(checkIn.sessionStartTime)}
                          </div>
                          <div>
                            Checked in: {formatDateTime(checkIn.checkedInAt)}
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteCheckIn(checkIn.id)}
                        disabled={deleteCheckInMutation.isPending}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        Delete
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-600">No check-ins found for this attendee</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Attendees List */}
      <div className="space-y-4">
        {attendees && attendees.length > 0 ? (
          attendees.map((attendee) => (
            <Card key={attendee.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-usc-garnet">{attendee.name}</h3>
                    <span className="px-3 py-1 bg-usc-gray-100 text-usc-gray-700 text-sm font-medium rounded-full">
                      {attendee.checkInCount} check-in{attendee.checkInCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="font-medium text-usc-gray-600">Email:</span>{' '}
                      <span className="text-usc-gray-900">{attendee.email}</span>
                    </div>
                    {attendee.affiliation && (
                      <div>
                        <span className="font-medium text-usc-gray-600">Affiliation:</span>{' '}
                        <span className="text-usc-gray-900">{attendee.affiliation}</span>
                      </div>
                    )}
                    <div>
                      <span className="font-medium text-usc-gray-600">Registered:</span>{' '}
                      <span className="text-usc-gray-900">{formatDateTime(attendee.registeredAt)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-2 ml-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewCheckIns(attendee.id)}
                  >
                    Check-ins ({attendee.checkInCount})
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewQRCode(attendee.id)}
                  >
                    QR Code
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(attendee)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDeleteAttendee(attendee)}
                    disabled={deleteAttendeeMutation.isPending}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))
        ) : (
          <Card className="p-12 text-center">
            <p className="text-usc-gray-600 text-lg">
              {searchTerm ? 'No attendees found matching your search' : 'No attendees found'}
            </p>
            <p className="text-usc-gray-500 mt-2">
              {searchTerm ? 'Try a different search term' : 'Create your first attendee to get started'}
            </p>
          </Card>
        )}
      </div>

      {/* Pagination Controls */}
      {pagination && pagination.totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-usc-gray-600">
            Showing page {pagination.page} of {pagination.totalPages} ({pagination.totalCount} total attendees)
          </p>
          <div className="flex space-x-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(1)}
              disabled={!pagination.hasPreviousPage}
            >
              First
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p: number) => Math.max(1, p - 1))}
              disabled={!pagination.hasPreviousPage}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p: number) => p + 1)}
              disabled={!pagination.hasNextPage}
            >
              Next
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(pagination.totalPages)}
              disabled={!pagination.hasNextPage}
            >
              Last
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
